// Public intake REST client — recipient-side of the send-form workflow.
//
// Token-authed: no JWT, no axios interceptor. Just fetch() against the
// API Gateway's /api/intake/{token} routes. Used by the public
// /intake/:token page; the operator dashboard talks to the same row via
// AppSync (operator side has its own auth model).
//
// The base URL is configurable via VITE_INTAKE_API_URL. Falls back to
// the inferred origin when unset so a dev server proxy can route /api
// to API Gateway without extra config.

const INTAKE_API_BASE = (
  import.meta.env.VITE_INTAKE_API_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  ""
).replace(/\/$/, "");

import type {
  CloverSecurityAndTransarmorInput,
  FeeOverridesInput,
  FleetOverridesInput,
} from "./types";

export interface IntakeFieldError {
  field: string;
  message: string;
}

export class IntakeApiError extends Error {
  /** Per-field validation errors when the server can attribute the
   *  failure to specific inputs. Always present (may be empty). */
  fieldErrors: IntakeFieldError[];

  constructor(
    public status: number,
    message: string,
    fieldErrors: IntakeFieldError[] = [],
  ) {
    super(message);
    this.name = "IntakeApiError";
    this.fieldErrors = fieldErrors;
  }
}

// Server view of the row, as returned by GET /api/intake/{token}. Mirrors
// intakeApiHandler._safe_load_view — never includes the raw sensitive
// values (recipient sees last-4 for previously-saved sensitive fields).
export interface IntakeLoadResponse {
  applicationID: string;
  status: string;
  businessName: string;
  merchantData: Record<string, unknown>;
  pdiPrefs: Record<string, unknown>;
  intakeRecipientType: "MERCHANT" | "TECH" | "BOTH" | "INTERNAL_SALES" | null;
  // merchantData paths the operator pre-filled. The form renders them
  // read-only; the API strips them from every save. Absent on older rows.
  lockedFields?: string[];
  intakeProgressPct: number;
  intakeOpensCount: number;
  intakeFirstOpenedAt: string | null;
  intakeLastOpenedAt: string | null;
  autoSubmit?: boolean;
  errorDetails?: string | null;
  // True when the operator dispatched this intake with mnspBundle=true.
  // Drives the form to render the MNSP network enrollment section
  // (register, gateway, network fees). False/absent when not requested.
  mnspEnrollmentRequested?: boolean;
  // Independent of mnspEnrollmentRequested -- true when the operator
  // dispatched with terminalRentalBundle=true. Drives the form to
  // render the Terminal Rental section (rental fee, terminal type, pin
  // pad). Either or both may be true.
  terminalRentalRequested?: boolean;
  // True when the admin filled the merchant data directly via
  // NewApplication and this intake token exists solely for the merchant
  // to sign the MNSP section. The form skips all merchant-data fields
  // and shows only the MNSP signing section.
  mnspOnly?: boolean;
  // The "recipient has completed their MNSP signing" signal for the
  // mnspOnly flow -- NOT status === "MNSP_SIGNED" (a bundled row's status
  // deliberately stays on the CardConnect track instead; see
  // isRecipientActionComplete in @/lib/intakeStatus). Stamped on sign,
  // cleared if an operator edit invalidates the MAR and requires a
  // re-sign, re-stamped once the merchant re-signs. Null/absent = not
  // signed yet (or awaiting re-sign).
  mnspEnrollmentSubmittedAt?: string | null;
  // Governs whether this application will ever be submitted to
  // CardConnect at all. Distinct from mnspOnly above -- an
  // MNSP_ENROLLMENT_ONLY row can be either mnspOnly=true (fill mode) or
  // mnspOnly=false (send mode, recipient fills the full form). Absent
  // on rows written before this field existed (treat as BUNDLED).
  applicationPath?: "BUNDLED" | "MNSP_ENROLLMENT_ONLY";
  // Document upload presence (issue #63). True once a presigned PUT was
  // issued for the kind; drives "uploaded" state after a refresh.
  hasDriversLicenseImage?: boolean;
  hasVoidedCheckImage?: boolean;
  // Short-lived presigned GET URLs (~1h) for the uploaded documents, so the
  // form can render a thumbnail of what's on file after a refresh and the
  // merchant can confirm they attached the right document. Null when
  // nothing's uploaded. The URL path carries the file extension
  // (.jpg/.png/.pdf) before the query string.
  driversLicenseImageUrl?: string | null;
  voidedCheckImageUrl?: string | null;
  // CardConnect signature URL -- populated AFTER successful boarding.
  // For auto-submit applications, this lands a few seconds after the
  // merchant hits Submit (SQS handler runs the CC create call); the
  // success page polls this endpoint and surfaces a "Sign now" button
  // once the URL is present. Null while pending.
  signatureUrl?: string | null;
  // Operator per-application auth-fee override (special-merchant pricing),
  // in dollars. Set by the operator on NewApplication; surfaced here so the
  // pricing-confirmation dialog shows the merchant the custom auth fee they
  // will be boarded on rather than the tier default. Null/absent = no
  // override, use the tier's standard auth fee.
  authFeeOverride?: number | null;
  // Admin manual fee overrides (subset of the tier fees), so the pricing
  // summary can reflect an overridden PCI annual / minimum processing fee
  // rather than the tier default. Null/absent = no overrides.
  feeOverrides?: FeeOverridesInput | null;
  // Effective interchange-plus discount %s (percent numbers, 0.15 == 0.15%):
  // the volume-tier base with any admin icPlusPricing override layered on per
  // brand, so the pricing summary shows the real discount. Null when volume is
  // missing. A per-brand value is null when the tier has no base for it.
  icPlusPricing?: {
    interchangeTypeCode: string;
    visaDiscountPct: number | null;
    mastercardDiscountPct: number | null;
    discoverDiscountPct: number | null;
    amexDiscountPct: number | null;
  } | null;
  // Admin fleet-card overrides (Voyager, WEX, PIN debit, EBT). Display-only,
  // same as feeOverrides -- never layered into the CardConnect boarding
  // payload -- but the paper MPA/CoPilot does show fleet pricing to the
  // merchant, so the pricing summary surfaces it as its own group
  // (pricingSummarySeparation). Null/absent = no fleet overrides set.
  fleetOverrides?: FleetOverridesInput | null;
  // Admin Clover Security + TransArmor overrides. Unlike fleetOverrides
  // above, this DOES reach the CardConnect boarding payload (merchant.
  // cloverSecurityAndTransarmor) -- but that's a submit-path concern; the
  // paper MPA/CoPilot shows these to the merchant too, so the pricing
  // summary surfaces them as its own group (pricingSummarySeparation).
  // Null/absent = no overrides set.
  cloverSecurityAndTransarmor?: CloverSecurityAndTransarmorInput | null;
  // Operator override of the Annual security bundle fee ($). Display-only
  // (our records only -- CardConnect has no API field for it); the pricing
  // summary shows it, falling back to the $199 tier default when null/absent.
  annualSecurityBundleFee?: number | null;
}

export interface IntakeSaveResponse {
  intakeProgressPct: number;
  status: string;
}

async function _request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${INTAKE_API_BASE}${path}`;
  const response = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Non-JSON body (e.g., API Gateway-level 502). Leave as null.
  }
  if (!response.ok) {
    const errBody = body as {
      error?: string;
      fieldErrors?: IntakeFieldError[];
    } | null;
    throw new IntakeApiError(
      response.status,
      errBody?.error || `HTTP ${response.status}`,
      Array.isArray(errBody?.fieldErrors) ? errBody!.fieldErrors : [],
    );
  }
  return body as T;
}

/** GET /api/intake/{token} — load the form + record an open beacon. */
export function loadIntake(token: string): Promise<IntakeLoadResponse> {
  return _request<IntakeLoadResponse>(
    `/api/intake/${encodeURIComponent(token)}`,
  );
}

/**
 * PATCH /api/intake/{token} — save a slice of merchant data.
 * The server merges the patch into the existing row's Merchant blob,
 * recalculates progress %, and pings the operator dashboard subscription.
 * Caller is responsible for debouncing rapid field changes (~1s typical).
 */
export function saveIntakeProgress(
  token: string,
  patch: {
    merchantData?: Record<string, unknown>;
    pdiPrefs?: Record<string, unknown>;
  },
): Promise<IntakeSaveResponse> {
  return _request<IntakeSaveResponse>(
    `/api/intake/${encodeURIComponent(token)}`,
    {
      method: "PATCH",
      body: JSON.stringify(patch),
    },
  );
}

/**
 * POST /api/intake/{token}/complete — recipient signals "done."
 * Transitions the row to READY_FOR_REVIEW. When the row was dispatched
 * with autoSubmit=true the server also enqueues an async boarding job;
 * the response's `autoSubmitted` flag tells the form which next-step
 * copy to render (signature-email coming vs. operator-will-review).
 */
export function completeIntake(
  token: string,
): Promise<{ status: string; autoSubmitted?: boolean }> {
  return _request<{ status: string; autoSubmitted?: boolean }>(
    `/api/intake/${encodeURIComponent(token)}/complete`,
    { method: "POST", body: "{}" },
  );
}

// ---- Document uploads (issue #63) -------------------------------------------

export type IntakeUploadKind = "DRIVERS_LICENSE" | "VOIDED_CHECK";

/**
 * Upload a merchant document (driver's license photo or voided check).
 *
 * Two hops: POST /upload-url gets a short-lived presigned S3 PUT (the
 * server records the S3 key on the row at issuance), then the file goes
 * straight to S3 so the bytes never transit the Lambda. Throws
 * IntakeApiError on the issuance step and Error on a failed PUT; the
 * caller treats the upload as complete only when this resolves.
 */
export async function uploadIntakeDocument(
  token: string,
  kind: IntakeUploadKind,
  file: File
): Promise<void> {
  const { uploadUrl } = await _request<{ uploadUrl: string }>(
    `/api/intake/${encodeURIComponent(token)}/upload-url`,
    {
      method: "POST",
      body: JSON.stringify({
        kind,
        contentType: file.type,
        contentLength: file.size,
      }),
    }
  );
  const put = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!put.ok) {
    throw new Error(`Upload failed (HTTP ${put.status}). Please try again.`);
  }
}
